import { create } from "zustand";

/** 05-SHARED.md's Toast row: "держится 1500ms (useCopyToClipboard resetMs)" —
 * the same hold `hooks/useCopyToClipboard.ts` defaults to. */
const HOLD_MS = 1500;

/** i18n keys under the `common` namespace that `<Toast/>` knows how to
 * show. A closed union rather than `string` — same reasoning as
 * `pages/friends/relationLabels.ts`: a dynamically-built `t(key)` call
 * type-checks regardless of what the generated `i18next.d.ts` says, so
 * writing the keys out by hand is what keeps a typo a compile error
 * instead of a silent runtime fallback. Add a member here when a second
 * caller needs one. */
export type ToastKey = "share.link_copied";

interface ToastState {
  /** The key currently showing, or `null` when no toast is up. */
  message: ToastKey | null;
  /** Show `key`; a call while a toast is already showing restarts the
   * 1500ms hold rather than stacking a second toast — "один на экран,
   * повторное действие перезапускает таймер." */
  show(key: ToastKey): void;
  /** Hide immediately and cancel any pending auto-hide. */
  hide(): void;
}

/**
 * UI state for the "action confirmed" toast (Task 7b, DUE-318) — the same
 * write-to-a-store-from-non-React-code pattern as `iosUiStore.ts`. The web
 * platform's `share()` clipboard fallback (`lib/platform/web.ts`) is the
 * first caller, since it has no native share sheet to confirm the copy.
 *
 * Only success confirmations go through this store — never errors, which
 * stay as on-screen copy per the design row.
 */
export const useToastStore = create<ToastState>((set) => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  function clearHoldTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  return {
    message: null,
    show: (key) => {
      clearHoldTimer();
      set({ message: key });
      timer = setTimeout(() => {
        timer = null;
        set({ message: null });
      }, HOLD_MS);
    },
    hide: () => {
      clearHoldTimer();
      set({ message: null });
    },
  };
});

/**
 * Test-only reset. The hold timer lives in a closure `setState` cannot
 * reach — resetting with `useToastStore.setState({ message: null })`
 * leaves a pending 1500ms timer that later nulls a *different* test's
 * message. Go through `hide()` instead, which clears it (final-review
 * Minor 7).
 */
export function __resetToastForTests(): void {
  useToastStore.getState().hide();
}
